import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, Eye, EyeSlash, CheckCircle, WarningCircle, ArrowLeft } from '@phosphor-icons/react';
import { formatApiErrorDetail } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';

import { API_URL } from '../config/api';

const pageAnimation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 },
};

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null); // null=checking, true/false
  const [tokenEmail, setTokenEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }
    // Verify token is valid before showing form
    axios.get(`${API_URL}/api/auth/reset-token-status/${token}`)
      .then(res => {
        setTokenValid(res.data.valid);
        setTokenEmail(res.data.email || '');
      })
      .catch(() => setTokenValid(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setSuccess(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    // Token checking state
    if (tokenValid === null) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B2545] mx-auto mb-3" />
          <p className="text-sm text-slate-500">Verifying reset token...</p>
        </div>
      );
    }

    // Invalid token
    if (!tokenValid) {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <WarningCircle size={22} weight="fill" className="flex-shrink-0" />
            <div>
              <p className="text-sm font-medium">Invalid or expired link</p>
              <p className="text-xs mt-0.5">This password reset link is no longer valid. Please request a new one.</p>
            </div>
          </div>
          <Link to="/forgot-password">
            <Button className="w-full bg-[#0B2545] hover:bg-[#13315C]" data-testid="request-new-reset">
              Request New Reset Link
            </Button>
          </Link>
          <Link to="/login">
            <Button variant="ghost" className="w-full text-slate-600" data-testid="invalid-back-to-login">
              <ArrowLeft size={18} className="mr-2" /> Back to Sign In
            </Button>
          </Link>
        </div>
      );
    }

    // Success state
    if (success) {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700">
            <CheckCircle size={22} weight="fill" className="flex-shrink-0" />
            <div>
              <p className="text-sm font-medium">Password reset successful</p>
              <p className="text-xs mt-0.5">You can now sign in with your new password.</p>
            </div>
          </div>
          <Link to="/login">
            <Button className="w-full bg-[#0B2545] hover:bg-[#13315C]" data-testid="success-go-to-login">
              Sign In
            </Button>
          </Link>
        </div>
      );
    }

    // Reset form
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {tokenEmail && (
          <p className="text-sm text-slate-500 text-center">
            Resetting password for <span className="font-medium text-slate-700">{tokenEmail}</span>
          </p>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm"
          >
            <WarningCircle size={18} weight="fill" />
            <span>{error}</span>
          </motion.div>
        )}

        <div className="space-y-2">
          <Label htmlFor="new-password" className="text-sm font-medium text-slate-700">
            New Password
          </Label>
          <div className="relative">
            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              id="new-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Minimum 8 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className="h-11 pl-10 pr-10"
              data-testid="reset-new-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(prev => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            >
              {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm-password" className="text-sm font-medium text-slate-700">
            Confirm Password
          </Label>
          <div className="relative">
            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              id="confirm-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className="h-11 pl-10"
              data-testid="reset-confirm-password-input"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={isLoading}
          className="w-full h-11 bg-[#0B2545] hover:bg-[#13315C] text-white font-medium"
          data-testid="reset-submit-button"
        >
          {isLoading ? 'Resetting...' : 'Reset Password'}
        </Button>

        <Link to="/login" className="block">
          <Button variant="ghost" className="w-full text-slate-600">
            <ArrowLeft size={18} className="mr-2" /> Back to Sign In
          </Button>
        </Link>
      </form>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <motion.div
        initial={pageAnimation.initial}
        animate={pageAnimation.animate}
        transition={pageAnimation.transition}
        className="relative z-10 w-full max-w-md"
      >
        <Card className="shadow-xl border-slate-200/60">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-16 h-16 bg-[#0B2545] rounded-xl flex items-center justify-center">
              <ShieldCheck size={32} weight="duotone" className="text-white" />
            </div>
            <CardTitle className="text-2xl font-bold tracking-tight text-slate-900">
              Set New Password
            </CardTitle>
            <CardDescription className="text-slate-600">
              Choose a strong password for your account
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            {renderContent()}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
